'use client'

import { useState, useEffect } from 'react'

import _isEqual from 'lodash/isEqual'
import _isNil from 'lodash/isNil'
import _isObject from 'lodash/isObject'

import DateUtils from '@/lib/date/date'
import { isClientSide, isServerSide } from '@/lib/html/server-side'
import { Logger } from '@/lib/logger/log'

const { parse, stringify } = JSON

const SESSION_STORAGE = 'sessionStorage'
const LOCAL_STORAGE = 'localStorage'

const STORAGE_CHANGE_EVENT = 'glammatic-storage-change'

interface StorageOptions {
  storageMechanism?: string
  subscribe?: boolean
  expiry?: Date | null
}

interface StorageValue<T = unknown> {
  value: T
  expiry: string
}

function isStorageAvailable(storageName: string): boolean {
  const hasWindowObj = typeof window !== 'undefined'
  return hasWindowObj && typeof window[storageName as keyof Window] !== 'undefined'
}

function getStorage(storageName: string): Storage {
  if (isStorageAvailable(storageName)) {
    return window[storageName as keyof Window] as Storage
  }
  throw Error(`Storage not available or unknown: ${storageName}`)
}

function dispatchStorageChangeEvent(key: string, storageMechanism: string): void {
  if (isClientSide()) {
    window.dispatchEvent(
      new CustomEvent(STORAGE_CHANGE_EVENT, { detail: { key, storageMechanism } })
    )
  }
}

export function removeFromStorage(key: string, storageMechanism = LOCAL_STORAGE): void {
  if (isStorageAvailable(storageMechanism)) {
    getStorage(storageMechanism).removeItem(key)

    dispatchStorageChangeEvent(key, storageMechanism)
  }
}

export function getFromStorage<T = unknown>(
  key: string,
  defaultValue: T | null = null,
  storageMechanism = LOCAL_STORAGE
): T | null {
  if (!isStorageAvailable(storageMechanism)) {
    return defaultValue
  }

  const storedValue = getStorage(storageMechanism).getItem(key)

  if (_isNil(storedValue)) {
    return defaultValue
  }

  try {
    const parsedValue = parse(storedValue)

    if (_isNil(parsedValue)) {
      return null
    }

    if (
      _isObject(parsedValue) &&
      Object.prototype.hasOwnProperty.call(parsedValue, 'expiry')
    ) {
      const storageObj = parsedValue as StorageValue<T>
      const expiryDate = DateUtils.parse(storageObj.expiry)
      if (DateUtils.now() > expiryDate) {
        removeFromStorage(key, storageMechanism)
        return defaultValue
      }
      return storageObj.value
    }
    return parsedValue
  } catch {
    return defaultValue
  }
}

export function listStorageKeys(storageMechanism = LOCAL_STORAGE): string[] {
  if (!isStorageAvailable(storageMechanism)) {
    return []
  }

  try {
    const storage = getStorage(storageMechanism)
    const keys: string[] = []

    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i)
      if (key) {
        keys.push(key)
      }
    }

    return keys || []
  } catch {
    return []
  }
}

export function setToStorage<T = unknown>(
  key: string,
  value: T,
  storageMechanism = LOCAL_STORAGE,
  expiry: Date | null = null
): void {
  if (_isNil(value)) {
    return
  }

  let finalValue: T | StorageValue<T> = value
  if (expiry && expiry instanceof Date) {
    finalValue = { value, expiry: expiry.toISOString() }
  }

  if (isStorageAvailable(storageMechanism)) {
    try {
      getStorage(storageMechanism).setItem(key, stringify(finalValue))

      dispatchStorageChangeEvent(key, storageMechanism)
    } catch {
      Logger.error(`Failed to store value for key "${key}":`, finalValue)
    }
  }
}

export default function useStorage<T = unknown>(
  key: string,
  defaultValue: T | null = null,
  { storageMechanism = LOCAL_STORAGE, subscribe = false }: StorageOptions = {}
) {
  const [storageLoaded, setStorageLoaded] = useState(false)
  const [value, setValue] = useState<T | null>(defaultValue)

  useEffect(() => {
    if (isServerSide()) {
      return
    }

    const valueStored = getFromStorage<T>(key, defaultValue, storageMechanism)
    setValue(valueStored)
    setStorageLoaded(true)

    function onStorageChangeListener(event: Event) {
      const customEvent = event as CustomEvent<{ key: string; storageMechanism: string }>
      const detail = customEvent.detail
      if (detail.key === key && detail.storageMechanism === storageMechanism) {
        const newValue = getFromStorage<T>(key, defaultValue, storageMechanism)
        if (!_isEqual(newValue, value)) {
          setValue(newValue)
        }
      }
    }

    if (subscribe) {
      window.addEventListener(STORAGE_CHANGE_EVENT, onStorageChangeListener)
    }

    return () => {
      if (subscribe) {
        window.removeEventListener(STORAGE_CHANGE_EVENT, onStorageChangeListener)
      }
    }
  }, [key, defaultValue, storageMechanism])

  if (isServerSide()) {
    return { value: defaultValue, setValue: () => {}, storageLoaded: false }
  }

  function setValueLocalStorage(
    newValue: T,
    { expiry = null }: { expiry?: Date | null } = {}
  ): void {
    if (storageLoaded) {
      setValue(newValue)
      setToStorage(key, newValue, storageMechanism, expiry)
    }
  }

  return { value, setValue: setValueLocalStorage, storageLoaded }
}

export { SESSION_STORAGE, LOCAL_STORAGE }
